<script setup lang="ts">
import { ref, computed } from 'vue'
import type { BuildingBlockDto } from '@/stores/buildingBlock'
import { normalized } from '@/utils/strings'
import DefaultIcon from '@/components/app/DefaultIcon.vue'

interface SelectedBlock extends BuildingBlockDto {
  uniqueKey: string
  categoryId: number
  categoryName: string
}

const props = defineProps<{
  selectedBlocks: SelectedBlock[]
  readonly: boolean
}>()

const emit = defineEmits<{
  removeBlock: [block: SelectedBlock]
}>()

const searchQuery = ref('')
const currentSelections = ref<SelectedBlock[]>([])

// use shared normalized helper

const groupedBlocks = computed(() => {
  const normalizedSearchQuery = normalized(searchQuery.value)
  const blocksByCategoryName = new Map<string, SelectedBlock[]>()

  for (const block of props.selectedBlocks) {
    if (
      normalizedSearchQuery &&
      !normalized(block.name).includes(normalizedSearchQuery) &&
      !normalized(block.categoryName).includes(normalizedSearchQuery)
    ) {
      continue
    }

    if (!blocksByCategoryName.has(block.categoryName)) {
      blocksByCategoryName.set(block.categoryName, [])
    }
    blocksByCategoryName.get(block.categoryName)!.push(block)
  }

  const groupedBlocksByCategory: Array<{ categoryName: string; blocks: SelectedBlock[] }> = []

  for (const entry of blocksByCategoryName.entries()) {
    groupedBlocksByCategory.push({
      categoryName: entry[0],
      blocks: entry[1]
    })
  }

  return groupedBlocksByCategory
})

const selectBlock = (block: SelectedBlock) => {
  const selectionIndex = currentSelections.value.findIndex(
    (selection) => selection.uniqueKey === block.uniqueKey
  )
  if (selectionIndex >= 0) {
    currentSelections.value.splice(selectionIndex, 1)
  } else {
    currentSelections.value.push(block)
  }
}

const removeCurrentSelection = () => {
  if (currentSelections.value.length === 0) return
  for (const block of currentSelections.value) {
    emit('removeBlock', block)
  }
  currentSelections.value = []
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <h2 class="text-h6">Selected building blocks</h2>
      <v-btn
        :disabled="currentSelections.length === 0 || props.readonly"
        @click="removeCurrentSelection"
        prepend-icon="mdi-minus"
        variant="flat"
        rounded="pill"
      >
        Remove{{ currentSelections.length > 1 ? ` (${currentSelections.length})` : '' }}
      </v-btn>
    </div>

    <v-text-field
      v-model="searchQuery"
      prepend-inner-icon="mdi-magnify"
      placeholder="Search..."
      variant="outlined"
      density="compact"
      hide-details
      class="mb-4"
    />

    <v-card variant="outlined" class="pa-0" min-height="260">
      <v-list density="compact" class="py-1">
        <template v-for="group in groupedBlocks" :key="group.categoryName">
          <!-- Category row -->
          <v-list-item class="bb-category-row" :ripple="false">
            <template #prepend>
              <v-icon size="small" color="cyan" class="mr-2"> mdi-minus </v-icon>
            </template>
            <v-list-item-title class="font-weight-medium">
              {{ group.categoryName }}
            </v-list-item-title>
          </v-list-item>

          <!-- Blocks under category -->
          <v-list-item
            v-for="block in group.blocks"
            :key="block.uniqueKey"
            class="bb-block-row px-6"
            :class="{
              'bb-block-row--active': currentSelections.some(
                (selection) => selection.uniqueKey === block.uniqueKey
              )
            }"
            :ripple="false"
            @click="selectBlock(block)"
          >
            <template #prepend>
              <v-icon size="x-small" class="mr-2" v-if="!block.hidden"> mdi-copyright </v-icon>
              <default-icon v-if="block.hidden" />
            </template>
            <v-list-item-title>{{ block.name }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>

      <div v-if="!groupedBlocks.length" class="pa-4 text-center text-medium-emphasis">
        No building blocks selected yet.
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.bb-category-row {
  min-height: 36px;
}

.bb-block-row {
  min-height: 32px;
  padding-left: 40px !important;
}

.bb-block-row--active {
  background-color: #b2ebf2;
}
</style>
